import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiMoneyDollarCircleLine,
  RiBankCardLine,
  RiCalendarLine,
  RiFileTextLine,
  RiBarcodeLine,
  RiHistoryLine,
  RiWallet3Line,
  RiNewspaperLine,
  RiStackLine,
  RiArrowLeftLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/release-order-payment`;
// Adjust this to match wherever your release order list endpoint actually lives
const RELEASE_ORDER_BASE_URL = `${process.env.REACT_APP_API_URL}/releaseOrder`;

const defaultForm = {
  amount: "",
  paymentMode: "cash",
  referenceNumber: "",
  chequeNumber: "",
  paymentDate: "",
  remarks: "",
};

const defaultNewPaymentForm = {
  releaseOrderId: "",
  itemId: "",
  ...defaultForm,
};

const paymentModes = ["cash", "cheque", "neft", "rtgs", "upi", "card", "other"];

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, wide, onBack }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${
          wide ? "max-w-3xl" : "max-w-2xl"
        } mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100 mr-1"
              >
                <RiArrowLeftLine className="w-4 h-4" />
              </button>
            )}
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const fieldIconMap = {
  amount: RiMoneyDollarCircleLine,
  paymentMode: RiBankCardLine,
  referenceNumber: RiBarcodeLine,
  chequeNumber: RiBarcodeLine,
  paymentDate: RiCalendarLine,
  remarks: RiFileTextLine,
};

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  options,
}) => {
  const Icon = fieldIconMap[name] || RiFileTextLine;
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
        {options ? (
          <select
            name={name}
            value={value}
            onChange={onChange}
            className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none capitalize"
          >
            {options.map((opt) => (
              <option key={opt} value={opt} className="capitalize">
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder || label}
            className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
          />
        )}
      </div>
    </div>
  );
};

const paymentFormFields = [
  { label: "Amount", name: "amount", type: "number" },
  { label: "Payment Mode", name: "paymentMode", options: paymentModes },
  { label: "Reference Number", name: "referenceNumber" },
  { label: "Cheque Number", name: "chequeNumber" },
  { label: "Payment Date", name: "paymentDate", type: "date" },
  { label: "Remarks", name: "remarks" },
];

const PaymentFormBody = ({ form, handleChange }) => (
  <div className="grid grid-cols-2 gap-4 mb-4">
    {paymentFormFields.map((f) => (
      <div key={f.name} className={f.name === "remarks" ? "col-span-2" : ""}>
        <Field {...f} value={form[f.name]} onChange={handleChange} />
      </div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    unpaid: "bg-red-50 border-red-200 text-red-600",
    partially_paid: "bg-amber-50 border-amber-200 text-amber-600",
    paid: "bg-emerald-50 border-emerald-200 text-emerald-600",
  };
  const labels = {
    unpaid: "Unpaid",
    partially_paid: "Partially Paid",
    paid: "Paid",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
        styles[status] || styles.unpaid
      }`}
    >
      {labels[status] || status}
    </span>
  );
};

const formatCurrency = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (val) =>
  val ? new Date(val).toLocaleDateString("en-IN") : "—";

// ReleaseOrderPayment.client is a populated "Bank" doc. The exact field
// holding the display name isn't confirmed, so this checks the most likely
// candidates in order. Trim this list down once you know the real field.
const getClientName = (payment) => {
  const client = payment?.client;
  if (!client) return "—";
  if (typeof client === "string") return "—"; // not populated
  return (
    client.name ||
    client.companyName ||
    client.bankName ||
    client.organisation ||
    client.accountHolderName ||
    "—"
  );
};

// item.media is a populated "Media" doc. Falls back to the snapshot
// `publication` string stored on the item itself.
const getItemLabel = (item) => {
  const mediaName =
    (item?.media &&
      typeof item.media === "object" &&
      item.media.organisation) ||
    item?.publication ||
    "Unknown Publication";
  return item?.edition ? `${mediaName} — ${item.edition}` : mediaName;
};

export default function AllReleaseOrderPayments() {
  const [payments, setPayments] = useState([]);
  const [releaseOrders, setReleaseOrders] = useState([]);
  const [releaseOrdersLoading, setReleaseOrdersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [entryForm, setEntryForm] = useState(defaultForm);
  const [newPaymentForm, setNewPaymentForm] = useState(defaultNewPaymentForm);
  const [newPaymentLedger, setNewPaymentLedger] = useState(null);
  const [newPaymentLedgerLoading, setNewPaymentLedgerLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}`);
      setPayments(res.data?.data || []);
      return res.data?.data || [];
    } catch {
      notify("Failed to fetch payments", "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchReleaseOrders = async () => {
    setReleaseOrdersLoading(true);
    try {
      const res = await axios.get(`${RELEASE_ORDER_BASE_URL}/list`);
      setReleaseOrders(res.data?.data || []);
    } catch {
      notify("Failed to fetch release orders", "error");
    } finally {
      setReleaseOrdersLoading(false);
    }
  };

  // Refetches the full list, then re-derives selectedPayment (and
  // optionally selectedItem) from the fresh data so nested modals stay in sync.
  const refreshSelected = async (paymentId, itemId) => {
    const list = await fetchPayments();
    const updatedPayment = list.find((p) => p._id === paymentId);
    if (updatedPayment) {
      setSelectedPayment(updatedPayment);
      if (itemId) {
        const updatedItem = updatedPayment.items.find(
          (it) => it._id === itemId,
        );
        if (updatedItem) setSelectedItem(updatedItem);
      }
    }
    return updatedPayment;
  };

  // --- Top-level "Add Payment" (new ledger / new RO) ---
  const openNewPayment = () => {
    setNewPaymentForm({
      ...defaultNewPaymentForm,
      paymentDate: new Date().toISOString().slice(0, 10),
    });
    setNewPaymentLedger(null);
    if (releaseOrders.length === 0) fetchReleaseOrders();
    setModal("newPayment");
  };

  const ensureLedgerForReleaseOrder = async (roId) => {
    setNewPaymentLedgerLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/ro/${roId}`);
      setNewPaymentLedger(res.data?.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        try {
          const createRes = await axios.post(`${BASE_URL}/${roId}`);
          setNewPaymentLedger(createRes.data?.data);
        } catch (createErr) {
          notify(
            createErr?.response?.data?.message ||
              "Failed to initialize payment ledger",
            "error",
          );
        }
      } else {
        notify("Failed to fetch payment ledger", "error");
      }
    } finally {
      setNewPaymentLedgerLoading(false);
    }
  };

  const handleNewPaymentROChange = (e) => {
    const roId = e.target.value;
    setNewPaymentForm((f) => ({ ...f, releaseOrderId: roId, itemId: "" }));
    setNewPaymentLedger(null);
    if (roId) ensureLedgerForReleaseOrder(roId);
  };

  const handleNewPaymentChange = (e) =>
    setNewPaymentForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreateNewPayment = async () => {
    if (!newPaymentForm.releaseOrderId) {
      notify("Select a release order", "error");
      return;
    }
    if (!newPaymentForm.itemId) {
      notify("Select a media / publication item", "error");
      return;
    }
    if (!newPaymentForm.amount || Number(newPaymentForm.amount) <= 0) {
      notify("Enter a valid payment amount", "error");
      return;
    }
    const { releaseOrderId, itemId, ...payload } = newPaymentForm;
    setActionLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/${releaseOrderId}/item/${itemId}/add`,
        payload,
      );
      notify("Payment added successfully");
      await fetchPayments();
      closeModal();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to add payment", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Row-level: manage an existing ledger's items ---
  const openManageItems = (payment) => {
    setSelectedPayment(payment);
    setModal("items");
  };

  const openAddItemPayment = (item) => {
    setSelectedItem(item);
    setForm({
      ...defaultForm,
      paymentDate: new Date().toISOString().slice(0, 10),
    });
    setModal("addItemPayment");
  };
  const backToItems = () => {
    setSelectedItem(null);
    setModal("items");
  };

  const openItemHistory = (item) => {
    setSelectedItem(item);
    setModal("itemHistory");
  };
  const backToItemHistory = () => {
    setSelectedEntry(null);
    setModal("itemHistory");
  };

  const openEditEntry = (entry) => {
    setSelectedEntry(entry);
    setEntryForm({
      amount: entry.amount ?? "",
      paymentMode: entry.paymentMode || "cash",
      referenceNumber: entry.referenceNumber || "",
      chequeNumber: entry.chequeNumber || "",
      paymentDate: entry.paymentDate
        ? new Date(entry.paymentDate).toISOString().slice(0, 10)
        : "",
      remarks: entry.remarks || "",
    });
    setModal("editEntry");
  };
  const openDeleteEntry = (entry) => {
    setSelectedEntry(entry);
    setModal("deleteEntry");
  };

  const openDeleteLedger = (payment) => {
    setSelectedPayment(payment);
    setModal("deleteLedger");
  };

  const closeModal = () => {
    setModal(null);
    setSelectedPayment(null);
    setSelectedItem(null);
    setSelectedEntry(null);
    setNewPaymentForm(defaultNewPaymentForm);
    setNewPaymentLedger(null);
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleEntryChange = (e) =>
    setEntryForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleAddItemPayment = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      notify("Enter a valid payment amount", "error");
      return;
    }
    const releaseOrderId =
      selectedPayment?.releaseOrder?._id || selectedPayment?.releaseOrder;
    const itemId = selectedItem?._id;
    setActionLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/${releaseOrderId}/item/${itemId}/add`,
        form,
      );
      notify("Payment added successfully");
      await refreshSelected(selectedPayment._id, itemId);
      setModal("items");
      setSelectedItem(null);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to add payment", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateEntry = async () => {
    setActionLoading(true);
    try {
      await axios.put(
        `${BASE_URL}/${selectedPayment._id}/item/${selectedItem._id}/entry/${selectedEntry._id}`,
        entryForm,
      );
      notify("Payment entry updated");
      await refreshSelected(selectedPayment._id, selectedItem._id);
      backToItemHistory();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to update entry", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEntry = async () => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${BASE_URL}/${selectedPayment._id}/item/${selectedItem._id}/entry/${selectedEntry._id}`,
      );
      notify("Payment entry deleted");
      await refreshSelected(selectedPayment._id, selectedItem._id);
      backToItemHistory();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to delete entry", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLedger = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/${selectedPayment._id}`);
      notify("Payment record deleted");
      fetchPayments();
      closeModal();
    } catch {
      notify("Failed to delete payment record", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = payments.filter((p) => {
    const clientName = getClientName(p);
    const matchesSearch =
      (p.roNumber || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.releaseOrder?.roNumber || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      clientName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const tableHeaders = [
    "RO No.",
    "Client",
    "Net Payable",
    "Paid",
    "Remaining",
    "Status",
    "Actions",
  ];

  // Items available to pick from for the currently selected release order
  // in the top-level "Add Payment" modal — excludes already fully-paid ones.
  const payableItems = (newPaymentLedger?.items || []).filter(
    (it) => it.status !== "paid",
  );
  const chosenItem = (newPaymentLedger?.items || []).find(
    (it) => it._id === newPaymentForm.itemId,
  );

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiWallet3Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Release Order Payments
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Payments to Media
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            >
              <option value="all">All Statuses</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
            </select>
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search RO no…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-52 transition"
              />
            </div>
            <button
              onClick={fetchPayments}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={openNewPayment}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Payment
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiWallet3Line className="w-4 h-4 text-blue-500" />
              All Release Order Payments
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map((label) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading payments…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiWallet3Line className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search || statusFilter !== "all"
                            ? "No payments match your filters."
                            : "No payment records found."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((p, i) => (
                    <tr
                      key={p._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {p.roNumber || p.releaseOrder?.roNumber || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {getClientName(p)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {formatCurrency(p.totalNetPayable)}
                      </td>
                      <td className="px-4 py-3 text-emerald-600 font-medium whitespace-nowrap">
                        {formatCurrency(p.totalAmountPaid)}
                      </td>
                      <td className="px-4 py-3 text-red-500 font-medium whitespace-nowrap">
                        {formatCurrency(p.totalRemainingAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => openManageItems(p)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold transition"
                          >
                            <RiStackLine className="w-3.5 h-3.5" /> Manage
                          </button>
                          <button
                            onClick={() => openDeleteLedger(p)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal === "newPayment" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Payment
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-1.5 mb-4">
            <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Release Order
            </label>
            <div className="relative">
              <RiNewspaperLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
              <select
                name="releaseOrderId"
                value={newPaymentForm.releaseOrderId}
                onChange={handleNewPaymentROChange}
                className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                <option value="">
                  {releaseOrdersLoading
                    ? "Loading release orders…"
                    : "Select a release order"}
                </option>
                {releaseOrders.map((ro) => (
                  <option key={ro._id} value={ro._id}>
                    {ro.roNumber} {ro.caption ? `— ${ro.caption}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {newPaymentForm.releaseOrderId && (
            <div className="flex flex-col gap-1.5 mb-4">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Media / Publication Item
              </label>
              <div className="relative">
                <RiStackLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                <select
                  name="itemId"
                  value={newPaymentForm.itemId}
                  onChange={handleNewPaymentChange}
                  disabled={newPaymentLedgerLoading}
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none disabled:opacity-60"
                >
                  <option value="">
                    {newPaymentLedgerLoading
                      ? "Loading items…"
                      : "Select an item"}
                  </option>
                  {payableItems.map((item) => (
                    <option key={item._id} value={item._id}>
                      {getItemLabel(item)} — Remaining{" "}
                      {formatCurrency(item.remainingAmount)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {chosenItem && (
            <div className="mb-4 flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm">
              <span className="text-slate-500">
                Net Payable:{" "}
                <span className="font-semibold text-slate-800">
                  {formatCurrency(chosenItem.netPayableAmount)}
                </span>
              </span>
              <span className="text-slate-500">
                Remaining:{" "}
                <span className="font-semibold text-red-500">
                  {formatCurrency(chosenItem.remainingAmount)}
                </span>
              </span>
            </div>
          )}

          <PaymentFormBody
            form={newPaymentForm}
            handleChange={handleNewPaymentChange}
          />

          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreateNewPayment}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Payment
            </button>
          </div>
        </Modal>
      )}

      {modal === "items" && (
        <Modal
          title={
            <>
              <RiStackLine className="w-5 h-5 text-blue-500" /> Items —{" "}
              {selectedPayment?.roNumber}
            </>
          }
          onClose={closeModal}
          wide
        >
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-[11px] uppercase text-blue-400 font-semibold">
                Net Payable
              </p>
              <p className="text-sm font-bold text-slate-800">
                {formatCurrency(selectedPayment?.totalNetPayable)}
              </p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <p className="text-[11px] uppercase text-emerald-500 font-semibold">
                Paid
              </p>
              <p className="text-sm font-bold text-emerald-700">
                {formatCurrency(selectedPayment?.totalAmountPaid)}
              </p>
            </div>
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100">
              <p className="text-[11px] uppercase text-red-400 font-semibold">
                Remaining
              </p>
              <p className="text-sm font-bold text-red-600">
                {formatCurrency(selectedPayment?.totalRemainingAmount)}
              </p>
            </div>
          </div>

          {!selectedPayment?.items || selectedPayment.items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              No items on this release order.
            </div>
          ) : (
            <div className="overflow-x-auto border border-blue-100 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50/50 border-b border-blue-100">
                    {[
                      "Publication",
                      "Net Payable",
                      "Paid",
                      "Remaining",
                      "Status",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedPayment.items.map((item) => (
                    <tr
                      key={item._id}
                      className="border-b border-blue-50 hover:bg-blue-50/40 transition"
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-medium">
                        {getItemLabel(item)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                        {formatCurrency(item.netPayableAmount)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-emerald-600 font-medium">
                        {formatCurrency(item.amountPaid)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-red-500 font-medium">
                        {formatCurrency(item.remainingAmount)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openAddItemPayment(item)}
                            disabled={item.status === "paid"}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <RiAddLine className="w-3.5 h-3.5" /> Add
                          </button>
                          <button
                            onClick={() => openItemHistory(item)}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiHistoryLine className="w-3.5 h-3.5" /> History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Close
            </button>
          </div>
        </Modal>
      )}

      {modal === "addItemPayment" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Payment —{" "}
              {getItemLabel(selectedItem || {})}
            </>
          }
          onClose={closeModal}
          onBack={backToItems}
        >
          <div className="mb-4 flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 text-sm">
            <span className="text-slate-500">
              Remaining:{" "}
              <span className="font-semibold text-red-500">
                {formatCurrency(selectedItem?.remainingAmount)}
              </span>
            </span>
            <StatusBadge status={selectedItem?.status} />
          </div>
          <PaymentFormBody form={form} handleChange={handleChange} />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={backToItems}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleAddItemPayment}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Payment
            </button>
          </div>
        </Modal>
      )}

      {modal === "itemHistory" && (
        <Modal
          title={
            <>
              <RiHistoryLine className="w-5 h-5 text-blue-500" /> History —{" "}
              {getItemLabel(selectedItem || {})}
            </>
          }
          onClose={closeModal}
          onBack={backToItems}
          wide
        >
          {!selectedItem?.paymentsMade ||
          selectedItem.paymentsMade.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              No payments recorded yet for this item.
            </div>
          ) : (
            <div className="overflow-x-auto border border-blue-100 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50/50 border-b border-blue-100">
                    {[
                      "Date",
                      "Amount",
                      "Mode",
                      "Reference",
                      "Cheque No.",
                      "Remarks",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-left text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...selectedItem.paymentsMade]
                    .sort(
                      (a, b) =>
                        new Date(b.paymentDate) - new Date(a.paymentDate),
                    )
                    .map((entry) => (
                      <tr
                        key={entry._id}
                        className="border-b border-blue-50 hover:bg-blue-50/40 transition"
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                          {formatDate(entry.paymentDate)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-semibold text-slate-800">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap capitalize text-slate-500">
                          {entry.paymentMode}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                          {entry.referenceNumber || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                          {entry.chequeNumber || "—"}
                        </td>
                        <td className="px-3 py-2 max-w-[140px] truncate text-slate-500">
                          {entry.remarks || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditEntry(entry)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition"
                            >
                              <RiEditLine className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openDeleteEntry(entry)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 transition"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={backToItems}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Close
            </button>
          </div>
        </Modal>
      )}

      {modal === "editEntry" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Payment
              Entry
            </>
          }
          onClose={closeModal}
          onBack={backToItemHistory}
        >
          <PaymentFormBody form={entryForm} handleChange={handleEntryChange} />
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={backToItemHistory}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdateEntry}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {modal === "deleteEntry" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Delete
              Payment Entry
            </>
          }
          onClose={closeModal}
          onBack={backToItemHistory}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete payment of{" "}
              <span className="text-red-500">
                {formatCurrency(selectedEntry?.amount)}
              </span>
              ?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This will recalculate the remaining amount and status for this
              item.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={backToItemHistory}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Entry
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === "deleteLedger" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete payment record for{" "}
              <span className="text-red-500">{selectedPayment?.roNumber}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This removes the entire payment ledger and history for every item
              on this release order. This action cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteLedger}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Record
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
